import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import type { IPhoneCallFilterCriteria } from '../../aida/viewModels/phonecall';

const BASE_QUERY_KEY = 'phonecall-filter';
const DEFAULT_PAGE_SIZE = 25;

export interface PhoneCallView {
	phoneCall: Api.IPhoneCall;
	company?: { id?: string; companyName?: string };
	contacts?: Api.IContact[];
}

export interface PhoneCallFilterData {
	calls: PhoneCallView[];
	totalCount: number | null;
}

export interface UsePhoneCallFilterQueryOptions {
	criteria: IPhoneCallFilterCriteria[];
	queryParams?: Record<string, string>;
	pageSize?: number;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

/** The endpoint is scoped to live-coaching calls server-side, so criteria never have to ask for that. */
export function usePhoneCallFilterQuery({
	criteria,
	queryParams,
	pageSize = DEFAULT_PAGE_SIZE,
	enabled = true,
	impersonationContext,
}: UsePhoneCallFilterQueryOptions) {
	const userSession = useUserSession();
	const accountId = userSession.account?.id;
	const impersonationKey = impersonationContext
		? { accountId: impersonationContext.account?.id, userId: impersonationContext.user?.id }
		: undefined;

	return useInfiniteQuery({
		queryKey: [BASE_QUERY_KEY, accountId, impersonationKey, queryParams, criteria, pageSize],
		queryFn: ({ pageParam }) =>
			userSession.webServiceHelper.callAsync<Api.IPagedCollection<PhoneCallView>>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'phonecall/filter',
					// `composeApiUrl` strips empty values, so the first page's '' cursor sends no param.
					queryParams: {
						pageSize: String(pageSize),
						pageToken: pageParam,
						...queryParams,
					},
					impersonationContext,
				}),
				'POST',
				{ op: Api.FilterOperator.And, criteria }
			),
		initialPageParam: '',
		getNextPageParam: lastPage => lastPage.pageToken ?? undefined,
		select: (data): PhoneCallFilterData => ({
			calls: data.pages.flatMap(page => page.values ?? []),
			totalCount: data.pages.at(-1)?.totalCount ?? null,
		}),
		enabled,
		// Keeps a refocus/remount from refetching every rep card at once.
		staleTime: 60_000,
		meta: {
			toastErrorMessage: 'Failed to load calls',
			errorLogging: { fn: 'usePhoneCallFilterQuery' },
		},
	});
}
